// Client half of the chart aggregation contract (issue #108).
//
// The three series endpoints — /api/meter/series, /api/station/series and
// /api/drift — decimate long ranges into at most ~300 points. That part is not
// negotiable: a 130 px tall, ~600 px wide panel cannot draw 16,279 readings, and
// at 7 d it would already be 13 readings per pixel.
//
// What the API now sends alongside each point is what this module reads:
//
//   ts_start / ts_end / n   the interval the point covers, and how many readings
//                           went into it — so nothing is captioned with one
//                           reading's clock while carrying 27 readings' mean
//   <field>_min / _max      the real extremes inside that interval, so a step
//                           the mean flattened is still visible as a band
//   is_latest               the newest raw reading, appended as its own point so
//                           the tip of the chart and the side panel agree
//
// Absent `_min`/`_max` means the bucket was flat, not that there is no envelope —
// callers fall back to the plotted value, which is why buildEnvelopes below does
// not require them.
//
// Everything here tolerates a response that carries none of it: fakeData.js
// generates raw series for the simulated tiers, and an older API would too.

// Reads the aggregation metadata off a series response. One place, so the three
// charts cannot caption the same response differently.
export function readAggregation(json) {
  const bucketSize = Number(json?.bucket_size) || 1;
  return {
    bucketSize,
    aggregated: json?.aggregated ?? bucketSize > 1,
    // Median interval one point covers, in ms. Null when nothing was aggregated.
    bucketSpanMs: Number.isFinite(json?.bucket_span_ms) ? json.bucket_span_ms : null,
    // Raw was asked for and refused because the range holds too many readings.
    rawCapped: json?.raw_capped === true,
    rawMaxPoints: Number.isFinite(json?.raw_max_points) ? json.raw_max_points : null,
    rows: Number.isFinite(json?.measurement_rows)
      ? json.measurement_rows
      : (Number.isFinite(json?.raw_readings) ? json.raw_readings : null),
  };
}

// "28 min", "3.5 h", "2 d". Deliberately coarse — the caption says "each point
// spans ~X", and a bucket boundary is not accurate to the second anyway.
export function formatSpan(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const minutes = ms / 60000;
  if (minutes < 1) return `${Math.max(1, Math.round(ms / 1000))} s`;
  if (minutes < 90) return `${Math.round(minutes)} min`;
  const hours = minutes / 60;
  if (hours < 48) return `${hours < 10 ? hours.toFixed(1) : Math.round(hours)} h`;
  return `${Math.round(hours / 24)} d`;
}

// Turns `<field>_min`/`_max` into the `[lo, hi]` pairs recharts wants for a
// range Area, under `<field>_band`.
//
// Only fields that carry an envelope somewhere in the series get one, so a raw
// series and a cumulative register (which has no envelope by design) add nothing
// to the data the chart walks. Where a single point is flat the band collapses
// onto the line rather than breaking, including at the appended raw tip — a null
// there would leave a notch in the band at the one point operators watch.
export function buildEnvelopes(data, fields) {
  if (!Array.isArray(data) || data.length === 0) return data;
  const banded = fields.filter((field) =>
    data.some((point) => typeof point[`${field}_min`] === "number")
  );
  if (banded.length === 0) return data;

  return data.map((point) => {
    const out = { ...point };
    for (const field of banded) {
      const value = point[field];
      if (typeof value !== "number") continue;   // absent stays absent
      out[`${field}_band`] = [
        point[`${field}_min`] ?? value,
        point[`${field}_max`] ?? value,
      ];
    }
    return out;
  });
}

// Whether a band is worth rendering for this field at all.
export function hasEnvelope(data, field) {
  return Array.isArray(data) && data.some((p) => Array.isArray(p[`${field}_band`]));
}

// The interval a point covers, for a tooltip. Returns null for a point that is a
// single reading — there the timestamp IS the measurement's, which is the whole
// distinction #108 was about.
export function pointInterval(point) {
  if (!point || !point.ts_start || !point.ts_end) return null;
  if ((point.n ?? 1) <= 1) return null;
  return { start: point.ts_start, end: point.ts_end, n: point.n };
}
